import _ from '@/utils/lodash-optimized'
import moment from 'moment'
import { createSelector } from 'reselect'

import * as Compartment from './compartment.js'

const codeToSymptomName = {
  22253000: 'Pain',
  48694002: 'Anxiety',
  267036007: 'Dyspnea',
  422587007: 'Nausea',
  62315008: 'Diarrhea',
  14760008: 'Constipation',
  422400008: 'Vomiting',
  79890006: 'Appetite',
  35489007: 'Depression',
  267031002: 'Tiredness',
  271782001: 'Drowsiness',
  82832008: 'Wellbeing'
}

const categoryCodeToColor = {
  301379001: 'green', // None
  40196000: 'blue', // Mild
  50415004: 'yellow', // Moderate
  76948002: 'orange', // Severe
  162471005: 'red' // Unbearable
}

export const symptomsList = [
  'Pain',
  'Nausea',
  'Constipation',
  'Tiredness',
  'Anxiety',
  'Vomiting',
  'Appetite',
  'Drowsiness',
  'Dyspnea',
  'Diarrhea',
  'Depression',
  'Wellbeing'
]

export const getLastReportObservationsIds = createSelector(
  Compartment.observations,
  symptoms =>
      // symptoms.find(s => s?.code?.coding?.[0]?.code === "451021004") || []
    _.chain(symptoms)
        .filter(s => s?.code?.coding?.[0]?.code === '451021004')
        .sortBy(p => p?.meta?.lastUpdated)
      .last()
      .get('related', [])
        .map(obs => obs?.target?.reference.split('Observation/')[1])
      .value()
)

export const getLastReportObservations = createSelector(
  [ Compartment.observations, getLastReportObservationsIds ],
  (allObservations, lastObservationsIds) =>
    allObservations.filter(o => lastObservationsIds.includes(o.id))
)

const getWithinLastThreeWeeks = createSelector(
  Compartment.observations,
  symptoms => {
    const dateThreeWeeksAgo = moment()
      .clone()
      .subtract(21, 'days')
      .startOf('day')

    const isWithinThreeWeeks = date =>
      moment(date).isAfter(dateThreeWeeksAgo)
    return symptoms.filter(
      o =>
          isWithinThreeWeeks(o?.meta?.lastUpdated) &&
          o?.component?.[1]
    )
  }
)

export const getSymptomsLastUpdateDatetime = createSelector(
  getWithinLastThreeWeeks,
  symptoms => {
    if (!symptoms) {
      return null
    }
    return _.chain(symptoms)
        .map(o => o?.meta?.lastUpdated)
      .sort()
      .last()
      .value()
  }
)

const getColorsStructure = (originalSymptoms, current) =>
  createSelector(originalSymptoms, symptoms => {
    const groupedSymptoms = _.groupBy(symptoms, s =>
        s?.code?.coding?.[0]?.code)

    const symptomsAndColors = {}

    Object.keys(groupedSymptoms).forEach(c => {
      const s = _.last(
        _.sortBy(groupedSymptoms[c], [ ss => ss.meta?.lastUpdated ])
      )

        symptomsAndColors[codeToSymptomName[s?.code?.coding?.[0]?.code]] =
        categoryCodeToColor[
            s?.component?.[1]?.valueCodeableConcept?.coding?.[0]?.code
        ]
    })

    const result = symptomsList.map(symptomName => ({
      name: symptomName,
      color: symptomsAndColors[symptomName] || null,
      current: (!!symptomsAndColors[symptomName] && current) || null
    }))
    return result
  })

export const getSymptomsAndColors = getColorsStructure(
  getLastReportObservations,
  true
)

export const getSymptomsAndColorsGray = getColorsStructure(
  getWithinLastThreeWeeks
)

export const getLastAlertFromObservations = createSelector(
  Compartment.observations,
  observations => {
    const last = _.chain(observations)
        .sortBy(o => o?.meta?.lastUpdated)
      .last()
      .value()
      const doAlert = (last?.extension ?? []).find(
        e => e.url === 'http://narusehealth.com/fhir/extensions/doalerts'
      )?.valueBoolean ?? false
      const author = (last?.performer?.[0]?.reference ?? 'Practitioner').startsWith('Practitioner')
    if (author) {
      return null
    }
    return doAlert
  }
)
